import { BarChart3, Shield, Users, Package, DollarSign, Target } from 'lucide-react';
import type {
  BenefitItem,
  HeroStat,
  MetricItem,
  MultiplierLever,
  MultiplierStep,
  NavLink,
  StepItem,
  TestimonialItem,
} from '@bright-web-skeleton/ui';
import type { FooterColumn } from '@bright-web-skeleton/ui/kloutsale';

export const navLinks: NavLink[] = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Results', href: '#results' },
  { label: 'Multiplier', href: '#multiplier' },
];

export const trustItems = ['Pay Only for Sales', 'Transparent Commissions', 'Shopify Integration'];

export const heroStats: HeroStat[] = [
  { value: 'Pay on Results', label: 'value before payment' },
  { value: 'Trusted Distribution', label: 'recommendation over interruption' },
  { value: 'You Stay in Control', label: 'your store, your identity' },
];

export const steps: StepItem[] = [
  {
    step: '01',
    title: 'Connect your store',
    description:
      'Sync your products, pricing, and inventory so Kloutsale works with what you have already built.',
  },
  {
    step: '02',
    title: 'Create offers worth sharing',
    description:
      'Set discounts and commissions that make sense for your margins and give trusted people a real reason to introduce your products.',
  },
  {
    step: '03',
    title: 'Track real results',
    description:
      'See which products, creators, and partners generate real sales, and pay only when value is actually created.',
  },
];

export const benefits: BenefitItem[] = [
  {
    title: 'Pay on results',
    icon: Target,
    description: 'Only pay when real purchases happen. Value comes first, payment follows.',
  },
  {
    title: 'Clear sales visibility',
    icon: BarChart3,
    description: 'Understand where sales come from and who creates them, without guesswork.',
  },
  {
    title: 'Trusted distribution',
    icon: Users,
    description:
      'Your products are introduced through people who already have attention and trust.',
  },
  {
    title: 'Store-connected products',
    icon: Package,
    description: 'Keep your inventory, pricing, and products up to date automatically.',
  },
  {
    title: 'Representation control',
    icon: Shield,
    description:
      'Decide who can represent your products, and maintain your brand standards on your terms.',
  },
  {
    title: 'Visible commissions',
    icon: DollarSign,
    description: 'Understand exactly how value is shared across each sale, with nothing hidden.',
  },
];

export const metrics: MetricItem[] = [
  { value: 'Easier Decisions', label: 'context replaces suspicion' },
  { value: 'Lower Risk', label: 'value created before payment' },
  { value: 'Aligned Effort', label: 'sellers earn when you earn' },
  { value: 'Lasting Visibility', label: 'attention that does not vanish' },
];

export const testimonials: TestimonialItem[] = [
  {
    name: 'A quieter way to grow',
    role: 'Observation',
    quote:
      'When effort and outcome are aligned, growth tends to feel less like pressure and more like a natural result of doing the work well.',
    metric: 'Alignment',
  },
  {
    name: 'From spending to sharing',
    role: 'Shift in approach',
    quote:
      'Instead of paying upfront for uncertain attention, brands can share value when something actually works. Confidence and control both improve.',
    metric: 'Confidence',
  },
  {
    name: 'Recommendation over interruption',
    role: 'How discovery is changing',
    quote:
      'Customers respond differently when a trusted person introduces a product. They start from context, not from suspicion.',
    metric: 'Context',
  },
];

export const multiplierSteps: MultiplierStep[] = [
  {
    title: 'Store',
    eyebrow: 'Your home base',
    description:
      'Your store gives you ownership, fulfillment, and identity. On its own, however, discovery often depends on ads, search, or waiting to be found.',
    points: [
      'You keep ownership and fulfillment',
      'You keep your customer relationship',
      'Discovery alone can depend on upfront spend',
    ],
  },
  {
    title: 'Marketplace',
    eyebrow: 'More exposure, less control',
    description:
      'Marketplaces can increase exposure, but they often weaken pricing power and place your brand inside someone else’s environment.',
    points: [
      'Exposure grows, ownership shrinks',
      'Pricing power becomes harder to hold',
      'The platform owns most of the discovery',
    ],
  },
  {
    title: 'Katalag',
    eyebrow: 'Distribution through trust',
    description:
      'Katalag changes how your offer travels. Discount helps the buyer act, commission motivates the seller, and trusted presence keeps the offer visible longer.',
    points: [
      'Discount makes the decision easier',
      'Commission gives sellers a real reason to share',
      'Trusted presence keeps the offer alive longer',
    ],
  },
];

export const multiplierLevers: MultiplierLever[] = [
  {
    title: 'Helps the buyer act',
    value: 'Discount',
    description:
      'A clear, exclusive deal lowers friction so the purchase feels easier to take, easier to share, and easier to repeat.',
    range: '5%–30%',
  },
  {
    title: 'Motivates the seller',
    value: 'Commission',
    description:
      'A meaningful commission gives creators and partners a real reason to feature your offer, explain it well, and keep returning to it.',
    range: '5%–25%',
  },
  {
    title: 'Keeps the offer visible',
    value: 'Ongoing Presence',
    description:
      'Because products stay present through catalogs and trusted people, attention lasts much longer than a single ad impression.',
    range: 'Compounding',
  },
];

export const footerColumns: FooterColumn[] = [
  {
    heading: 'For Brands',
    links: [
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Features', href: '#features' },
      { label: 'Multiplier', href: '#multiplier' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Press', href: '/press' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Help Center', href: '/help-center' },
      { label: 'Contact', href: '/contact' },
      { label: 'API Docs', href: '/api-docs' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
];
